import React from "react";
import {
  cn,
  getBackgroundStyles,
  getForegroundStyles,
  incPanelColorLevel,
} from "../../styles";
import { BaseElementProps, PanelColor, ThemeColor } from "../../props";
import { Icon, IconProps, Typography, TypographyContent } from "../../atoms";
import { ThemeColorIcons } from "../../constants";
import { Panel, PanelProps } from "../../layouts";

export interface StatusCardProps extends BaseElementProps {
  /**
   * Additional content shown below the title and above the actions.
   */
  children?: React.ReactNode;

  /**
   * Props passed to the background panel.
   */
  panelProps?: PanelProps;

  /**
   * Props passed to the default icon symbol.
   */
  iconProps?: IconProps;

  /**
   * The thematic color of the symbol.
   * Should be a value from the imported `ThemeColor` enum.
   * @default ThemeColor.gray
   */
  color?: ThemeColor;

  /**
   * Main heading text displayed prominently at the top.
   * @default "Title"
   */
  title?: TypographyContent;

  /**
   * Secondary descriptive text displayed below the title.
   * Supports multi-line text with line breaks.
   * @default "Subtitle"
   */
  subtitle?: TypographyContent;

  /**
   * Action buttons or interactive elements displayed at the bottom right.
   * Typically contains buttons for user actions like "Retry", "Cancel", etc.
   */
  actions?: React.ReactNode;

  /**
   * Render prop function for custom symbol display.
   * Receives color and responsive size classes to apply consistent theming.
   * @default "Renders thematic appropriate icon"
   */
  renderSymbol?: (props: {
    color: ThemeColor;
    sizeClassName: string;
    dense: boolean;
  }) => React.ReactNode;

  /**
   * Additional CSS class names to apply to the symbol background circle.
   * Can be used to override background colors, add gradients, shadows, or other styling.
   * Applied after default classes, allowing for easy customization.
   */
  symbolBackgroundClassName?: string;
}

/**
 * StatusCard component for displaying status messages, empty states, or informational content in a card layout.
 * Provides a flexible card-like container with a symbol indicator (for Icon, CircularProgressor or custom content), title, subtitle, and optional actions.
 * Features responsive design that adapts layout from vertical (mobile) to horizontal (desktop).
 * Automatically displays an appropriate icon for the ThemeColor if no renderSymbol is provided.
 */
export const StatusCard = ({
  title = { local: "Title" },
  subtitle = { local: "Subtitle" },
  actions,
  iconProps,
  panelProps,
  renderSymbol = null,
  symbolBackgroundClassName,
  color = ThemeColor.none,
  children,
  dense = false,
  className,
}: StatusCardProps) => {
  const symbolSizeClassName = dense
    ? "w-8 h-8 sm:w-10 sm:h-10"
    : "w-12 h-12 sm:w-16 sm:h-16";

  const hasSubtitle = subtitle != null && subtitle !== false;
  const hasChildren = Boolean(children);
  const hasExtraContent = hasSubtitle || hasChildren || Boolean(actions);

  const symbol = renderSymbol ? (
    renderSymbol({
      color,
      sizeClassName: symbolSizeClassName,
      dense,
    })
  ) : (
    <Icon
      path={ThemeColorIcons[color]}
      color={color}
      muted={(iconProps?.color || color) === ThemeColor.none}
      {...iconProps}
      className={cn(symbolSizeClassName, iconProps?.className)}
    />
  );

  return (
    <Panel
      {...panelProps}
      className={cn(
        "flex flex-col sm:flex-row w-full relative",
        hasExtraContent ? "items-start" : "items-center",
        dense
          ? "max-w-2xl gap-4 sm:gap-6 px-3 sm:px-3 py-4 sm:py-3"
          : "max-w-3xl gap-8 sm:gap-8 px-4 sm:px-8 py-8 sm:py-8",
        panelProps?.className,
        className
      )}
    >
      {symbol && (
        <div
          className={cn(
            "flex items-center justify-center rounded-full flex-shrink-0",
            getBackgroundStyles(
              incPanelColorLevel(
                (panelProps?.color as PanelColor) || PanelColor.level1
              )
            ),
            getForegroundStyles(
              incPanelColorLevel(
                (panelProps?.color as PanelColor) || PanelColor.level1
              )
            ),
            dense
              ? "w-12 h-12 sm:w-16 sm:h-16 mb-4 sm:mb-0"
              : "w-20 h-20 sm:w-24 sm:h-24 mb-6 sm:mb-0",
            symbolBackgroundClassName
          )}
        >
          {symbol}
        </div>
      )}
      <div className="w-full break-words overflow-hidden">
        <Typography.P
          className={cn(
            "font-medium",
            dense ? "text-lg sm:text-xl" : "text-xl sm:text-2xl",
            hasExtraContent ? (dense ? "mb-2" : "mb-4") : "mb-0"
          )}
          content={title}
        />
        {hasSubtitle && (
          <Typography.P
            muted
            className={cn(
              "whitespace-pre-line break-words",
              dense ? "text-xs sm:text-sm" : "text-sm sm:text-base"
            )}
            content={subtitle}
          />
        )}
        {hasChildren && (
          <div className={dense ? "my-2" : "my-4"}>{children}</div>
        )}
        {actions && (
          <div
            className={cn("flex justify-end gap-3", dense ? "mt-4" : "mt-8")}
          >
            {actions}
          </div>
        )}
      </div>
    </Panel>
  );
};

StatusCard.displayName = "StatusCard";
